import Decimal from 'decimal.js'

import { formatUsd, parseDecimal } from '@/lib/decimal-format'
import type { AccountSummary } from '@/types/generated/common/account'

export { formatUsd, parseDecimal }

/**
 * Format an authoritative net/gross leverage (Decimal-as-string) as "2.50x".
 * Renders "—" when there is no equity denominator: `equityUsd` is "" when the
 * venue can't value the account right now, and "0" is a genuinely empty account,
 * against which leverage means nothing. A genuine zero leverage with real equity
 * (a flat book after a full close) renders "0.00x", not "—".
 */
export function formatLeverage(
  leverage: string | null | undefined,
  equityUsd: string | null | undefined
): string {
  const equity = parseDecimal(equityUsd)
  if (equity === null || equity.isZero()) return '—'
  const lev = parseDecimal(leverage)
  if (lev === null) return '—'
  return `${lev.toFixed(2)}x`
}

/**
 * Below this fraction of gross notional, |net| is treated as directional noise —
 * a delta-neutral (hedged) book whose net sits at ~0. The backend recomputes
 * net against the live mark each tick, so a hedged book's net jitters across zero;
 * without a deadband the long/short chip flips back and forth. 1% of gross is well
 * clear of a deliberately neutral book (sub-0.5%) yet far below any real direction.
 */
const NEUTRAL_NET_FRACTION = new Decimal('0.01')

/**
 * Long/short bias from the signed net notional (Σ delta_usd). Returns null when
 * the book is flat, the value is unparseable, or |net| is within
 * `NEUTRAL_NET_FRACTION` of gross notional (effectively delta-neutral) — so the
 * caller omits the chip rather than flickering long↔short across zero. Passing no
 * gross keeps the legacy exact-zero behavior.
 */
export function notionalBias(
  netNotionalUsd: string | null | undefined,
  grossNotionalUsd?: string | null | undefined
): 'long' | 'short' | null {
  const net = parseDecimal(netNotionalUsd)
  if (net === null || net.isZero()) return null
  const gross = parseDecimal(grossNotionalUsd)
  if (gross !== null && gross.isPositive() && net.abs().lte(gross.mul(NEUTRAL_NET_FRACTION))) {
    return null
  }
  return net.isPositive() ? 'long' : 'short'
}

export type PnlTimeframe = '1D' | '1W' | '1M' | 'ALL'

type PnlTimeframeField = 'pnl1dUsd' | 'pnl1wUsd' | 'pnl1mUsd' | 'pnlAllUsd'

type AccountSummaryPnlFields = Partial<
  Pick<AccountSummary, PnlTimeframeField | 'dailyUnrealizedPnlUsd'>
>

const TIMEFRAME_TO_PNL_FIELD: Record<PnlTimeframe, PnlTimeframeField> = {
  '1D': 'pnl1dUsd',
  '1W': 'pnl1wUsd',
  '1M': 'pnl1mUsd',
  ALL: 'pnlAllUsd',
}

/**
 * Headline timeframe PnL for the account card. Prefers the server-computed
 * true PnL for the selected window (equity delta net of transfers —
 * structurally immune to deposits/withdrawals). That field is per-venue
 * gated and absent until a venue's transfers gate is live, so absence must
 * fall back, never read as zero. For 1D specifically, falls back next to the
 * older `dailyUnrealizedPnlUsd` (positions marked-to-market only, no
 * transfer-immunity) before falling back to the caller's estimate — 1W/1M/ALL
 * have no such legacy field and go straight to the estimate.
 */
export function resolveHeadlinePnl(
  selectedTimeframe: PnlTimeframe,
  accountSummary: AccountSummaryPnlFields | null | undefined,
  fallbackPnl: number
): number {
  const exact = parseDecimal(accountSummary?.[TIMEFRAME_TO_PNL_FIELD[selectedTimeframe]])
  if (exact !== null) return exact.toNumber()
  if (selectedTimeframe === '1D') {
    const legacy = parseDecimal(accountSummary?.dailyUnrealizedPnlUsd)
    if (legacy !== null) return legacy.toNumber()
  }
  return fallbackPnl
}

/**
 * Client-side PnL fallback used when `resolveHeadlinePnl` has no server field
 * for the selected window (see its own doc comment). The windowed portfolio
 * chart's own delta (`values.at(-1) - values[0]` over that timeframe's
 * window) is the right signal for ANY timeframe when available — it's a real
 * equity delta over the actual selected window. `dailyPnlFromBalances` (a 24h
 * estimate derived from spot balances' 24h ticker change) is only ever a
 * stand-in for 1D specifically when chart data isn't available yet; it must
 * never substitute for a longer window (using a 1-day estimate while labeled
 * "1W" is misleading), and it inherently only reflects spot balance price
 * movement, not full account PnL (it misses derivatives), so it's a weaker
 * signal than the chart delta even for 1D.
 */
export function resolvePnlFallback(
  timeframe: PnlTimeframe,
  windowedChartDelta: number | null,
  dailyPnlFromBalances: number
): number {
  if (windowedChartDelta !== null) return windowedChartDelta
  if (timeframe === '1D') return dailyPnlFromBalances
  return 0
}
